<template>
    <div class="page">
        <IndexHeader />

        <main class="page-main">
            <!-- 无 tenant_key 错误提示 -->
            <div v-if="!tenantKey" class="error-card">
                <div class="error-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M12 9V13M12 17H12.01M5.07 19H18.93C20.47 19 21.44 17.33 20.66 16L13.73 4C12.96 2.67 11.04 2.67 10.27 4L3.34 16C2.56 17.33 3.53 19 5.07 19Z"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </div>
                <h2>访问错误</h2>
                <p>缺少租户标识（tenant_key），无法加载小店内容。</p>
                <p class="error-hint">请通过正确的租户小店链接访问。</p>
            </div>

            <!-- 商品详情页 -->
            <div v-else class="shop-container">
                <!-- 顶部操作栏 -->
                <div class="detail-top-bar">
                    <div class="detail-back" @click="goBack">
                        <LeftOutlined /> 返回商品列表
                    </div>
                    <a-button class="orders-btn" size="small" @click="goOrders">
                        订单查询
                    </a-button>
                </div>

                <!-- 商品详情卡片 -->
                <div class="detail-card" v-if="productDetail">
                    <div class="detail-images">
                        <a-image v-if="productDetail.goods_thumb" :src="productDetail.goods_thumb"
                            class="detail-main-img" :preview="{ srcList: detailImages }" />
                    </div>
                    <div class="detail-info">
                        <h1 class="detail-title">{{ productDetail.goods_name }}</h1>
                        <div class="detail-price-row">
                            <span class="detail-price">￥{{ productDetail.goods_price }}</span>
                        </div>
                        <div class="detail-meta">
                            <span v-if="productDetail.goods_stock === -1">库存：充足</span>
                            <span v-else>库存：{{ productDetail.goods_stock }}</span>
                            <span>起购：{{ productDetail.min_order_num }}</span>
                            <span>限购：{{ productDetail.max_order_num }}</span>
                        </div>
                        <p class="detail-desc" v-if="productDetail.goods_desc"
                            v-html="formatDesc(productDetail.goods_desc)"></p>
                        <div class="detail-order">
                            <a-input-number v-model:value="orderForm.quantity" :min="productDetail.min_order_num || 1"
                                :max="productDetail.max_order_num || 99999" :step="1" />
                            <span class="order-total">合计：￥{{ orderAmount }}</span>
                        </div>
                        <a-button type="primary" class="buy-btn" @click="goCreateOrder">立即购买</a-button>
                    </div>
                </div>

                <!-- 商品详情内容 -->
                <div class="detail-content" v-if="productDetail && productDetail.goods_detail"
                    v-html="productDetail.goods_detail"></div>

                <!-- 下单弹窗 -->
                <a-modal v-model:open="showOrder" title="确认订单" width="600px" :mask-closable="false"
                    class="order-dialog" v-if="productDetail" :footer="null">
                    <div class="order-product">
                        <a-image v-if="productDetail.goods_thumb" :src="productDetail.goods_thumb"
                            class="order-product-img" />
                        <div class="order-product-info">
                            <h3>{{ productDetail.goods_name }}</h3>
                            <span class="order-product-price">￥{{ productDetail.goods_price }} / {{
                                productDetail.goods_unit || '个' }}</span>
                        </div>
                    </div>
                    <a-form :model="orderForm" layout="vertical" class="order-form">
                        <a-form-item label="购买数量" required>
                            <a-input-number v-model:value="orderForm.quantity" :min="productDetail.min_order_num || 1"
                                :max="productDetail.max_order_num || 99999" />
                        </a-form-item>
                        <a-form-item label="授权字段" required>
                            <a-input v-model:value="orderForm.auth_value"
                                :placeholder="productDetail.auth_field_placeholder || '请输入授权字段值'" />
                        </a-form-item>
                        <a-form-item label="支付方式" required>
                            <div class="pay-methods">
                                <div class="pay-method-item" :class="{ active: orderForm.pay_type === 'alipay' }"
                                    v-if="payMethods.alipay" @click="orderForm.pay_type = 'alipay'">
                                    <span class="pay-icon alipay">支</span>
                                    <span>支付宝</span>
                                </div>
                                <div class="pay-method-item" :class="{ active: orderForm.pay_type === 'wechat' }"
                                    v-if="payMethods.wechat" @click="orderForm.pay_type = 'wechat'">
                                    <span class="pay-icon wechat">微</span>
                                    <span>微信</span>
                                </div>
                                <div class="pay-method-item" :class="{ active: orderForm.pay_type === 'qq' }"
                                    v-if="payMethods.qq" @click="orderForm.pay_type = 'qq'">
                                    <span class="pay-icon qq">Q</span>
                                    <span>QQ</span>
                                </div>
                                <div class="pay-method-item" :class="{ active: orderForm.pay_type === 'usdt' }"
                                    v-if="payMethods.usdt" @click="orderForm.pay_type = 'usdt'">
                                    <span class="pay-icon usdt">U</span>
                                    <span>USDT</span>
                                </div>
                            </div>
                        </a-form-item>
                        <a-form-item label="邮箱" required>
                            <a-input v-model:value="orderForm.email" placeholder="请输入邮箱，用于绑定授权并自动创建账号" />
                        </a-form-item>
                        <a-form-item label="查询密码" required>
                            <a-input v-model:value="orderForm.query_password" type="text" placeholder="设置订单查询密码（不少于6位）" />
                        </a-form-item>
                    </a-form>
                    <div class="order-summary">
                        <span>应付金额：</span>
                        <span class="order-amount">￥{{ orderAmount }}</span>
                    </div>
                    <a-button type="primary" class="submit-order-btn" :loading="submitting" @click="submitOrder">
                        提交订单
                    </a-button>
                </a-modal>
            </div>
        </main>

        <IndexFooter />
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import { message } from 'ant-design-vue'
import IndexHeader from '@/components/IndexHeader.vue'
import IndexFooter from '@/components/IndexFooter.vue'

const baseURL = process.env.VUE_APP_API_BASE_URL;

export default defineComponent({
    name: 'ShopDetail',
    components: { IndexHeader, IndexFooter },
    data() {
        return {
            tenantKey: '',
            goodsId: null,
            productDetail: null,
            showOrder: false,
            orderForm: { quantity: 1, auth_value: '', pay_type: '', email: '', query_password: '' },
            payMethods: { alipay: false, wechat: false, qq: false, usdt: false },
            submitting: false,
        }
    },
    computed: {
        detailImages() {
            if (!this.productDetail) return [];
            const imgs = this.productDetail.goods_images || [];
            const thumb = this.productDetail.goods_thumb;
            return thumb ? [thumb, ...imgs] : imgs;
        },
        orderAmount() {
            if (!this.productDetail) return 0;
            return (this.productDetail.goods_price * this.orderForm.quantity).toFixed(2);
        }
    },
    mounted() {
        this.tenantKey = this.$route.query.tenant_key || '';
        this.goodsId = this.$route.query.goods_id || null;
        if (this.tenantKey && this.goodsId) {
            this.getProductDetail();
        }
    },
    methods: {
        formatDesc(text) {
            if (!text) return '';
            return text.replace(/\n/g, '<br/>');
        },
        goBack() {
            this.$router.push({ path: '/shop', query: { tenant_key: this.tenantKey } });
        },
        goOrders() {
            this.$router.push({ path: '/shopOrders', query: { tenant_key: this.tenantKey } });
        },
        async getProductDetail() {
            try {
                const res = await axios.post(`${baseURL}/api/shop/product/detail`, {
                    tenant_key: this.tenantKey,
                    goods_id: this.goodsId,
                });
                if (res.data.code === 0 || res.data.code === 200) {
                    this.productDetail = res.data.data;
                    this.orderForm.quantity = this.productDetail.min_order_num || 1;
                    this.$nextTick(() => {
                        const main = this.$el.querySelector('.page-main');
                        if (main) main.scrollTo({ top: 0, behavior: 'smooth' });
                    });
                } else {
                    message.error(res.data.msg || '获取商品详情失败');
                }
            } catch (e) {
                console.error('获取商品详情失败:', e);
                message.error('获取商品详情失败');
            }
        },
        goCreateOrder() {
            this.showOrder = true;
            this.getPayMethods();
        },
        async getPayMethods() {
            try {
                const res = await axios.post(`${baseURL}/api/shop/pay/methods`, { tenant_key: this.tenantKey });
                if (res.data.code === 0 || res.data.code === 200) {
                    this.payMethods = res.data.data || {};
                    // 检查是否有可用的支付方式
                    const methods = ['alipay', 'wechat', 'qq', 'usdt'];
                    const hasAvailable = methods.some(m => this.payMethods[m]);
                    if (!hasAvailable) {
                        message.error('暂无可用的支付方式');
                        this.showOrder = false;
                        return;
                    }
                    // 自动选中第一个支持的支付方式
                    if (!this.orderForm.pay_type) {
                        for (const m of methods) {
                            if (this.payMethods[m]) {
                                this.orderForm.pay_type = m;
                                break;
                            }
                        }
                    }
                } else {
                    message.error(res.data.msg || '获取支付方式失败');
                }
            } catch (e) {
                console.error('获取支付方式失败:', e);
                message.error('获取支付方式失败');
            }
        },
        async submitOrder() {
            if (!this.orderForm.auth_value) {
                message.warning('请输入授权字段');
                return;
            }
            if (!this.orderForm.pay_type) {
                message.warning('请选择支付方式');
                return;
            }
            if (!this.orderForm.email) {
                message.warning('请输入邮箱');
                return;
            }
            if (!this.orderForm.query_password || this.orderForm.query_password.length < 6) {
                message.warning('查询密码不能少于6位');
                return;
            }
            this.submitting = true;
            try {
                const res = await axios.post(`${baseURL}/api/shop/order/create`, {
                    tenant_key: this.tenantKey,
                    goods_id: this.productDetail.goods_id,
                    email: this.orderForm.email,
                    auth_value: this.orderForm.auth_value,
                    pay_type: this.orderForm.pay_type,
                    query_password: this.orderForm.query_password,
                    quantity: this.orderForm.quantity,
                });
                if (res.data.code === 0 || res.data.code === 200) {
                    const data = res.data.data;
                    // 缓存订单
                    this.cacheOrder({
                        order_id: data.order_id,
                        query_password: this.orderForm.query_password,
                        goods_name: this.productDetail.goods_name,
                        amount: data.amount || (this.productDetail.goods_price * this.orderForm.quantity).toFixed(2),
                        status: 0,
                        created_at: new Date().toLocaleString(),
                    });
                    if (data.pay_type === 'alipay_dmf') {
                        // 当面付：跳转到小店当面付页面，前端根据pay_url生成二维码
                        message.success('订单创建成功，即将跳转支付');
                        setTimeout(() => {
                            this.$router.push({
                                path: '/shopAlipayDMF',
                                query: {
                                    tenant_key: this.tenantKey,
                                    order_id: data.order_id,
                                    query_password: this.orderForm.query_password,
                                }
                            });
                        }, 1000);
                    } else if (data.pay_url) {
                        // 易支付/网站支付：直接跳转pay_url
                        message.success('订单创建成功，即将跳转支付');
                        setTimeout(() => { window.location.href = data.pay_url; }, 1000);
                    } else {
                        message.success('下单成功');
                    }
                } else {
                    message.error(res.data.msg || '下单失败');
                }
            } catch (e) {
                console.error('下单失败:', e);
                message.error('下单失败，请稍后重试');
            } finally {
                this.submitting = false;
            }
        },
        // 订单缓存
        cacheOrder(order) {
            const key = `shop_orders_${this.tenantKey}`;
            const raw = localStorage.getItem(key);
            let list = raw ? JSON.parse(raw) : [];
            list.unshift(order);
            if (list.length > 20) list.pop();
            localStorage.setItem(key, JSON.stringify(list));
        },
    }
})
</script>

<style scoped lang="scss">
.page {
    min-height: 100vh;
    background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 60%, #f8fafc 100%);
    display: flex;
    flex-direction: column;
}

.page-main {
    max-width: 1100px;
    margin: 0 auto;
    padding: 12vh 1.5rem 4rem;
    display: flex;
    flex-direction: column;
    height: 90vh;
    overflow-y: auto;
    width: 100%;
    box-sizing: border-box;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.page-main::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
}

/* 错误提示 */
.error-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 4rem 2rem;
    background: #fff;
    border-radius: 1.4rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 22px 45px -30px rgba(239, 68, 68, 0.25);

    .error-icon {
        width: 64px;
        height: 64px;
        color: #ef4444;
        margin-bottom: 1rem;

        svg {
            width: 100%;
            height: 100%;
        }
    }

    h2 {
        font-size: 1.5rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 0.5rem;
    }

    p {
        font-size: 0.95rem;
        color: #64748b;
        margin: 0.2rem 0;
    }

    .error-hint {
        color: #94a3b8;
        font-size: 0.85rem;
        margin-top: 0.5rem;
    }
}

/* 顶部操作栏 */
.detail-top-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
}

/* 返回按钮 */
.detail-back {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    color: #2563eb;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
}

.orders-btn {
    border: 1px solid rgba(37, 99, 235, 0.3);
    color: #2563eb;
    background: rgba(37, 99, 235, 0.05);

    &:hover {
        background: rgba(37, 99, 235, 0.12);
    }
}

/* 商品详情 */
.detail-card {
    display: flex;
    gap: 2rem;
    background: #fff;
    border-radius: 1.2rem;
    padding: 2rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 12px 24px -16px rgba(15, 23, 42, 0.2);

    @media (max-width: 768px) {
        flex-direction: column;
        padding: 1.2rem;
        gap: 1rem;
    }
}

.detail-main-img {
    width: 300px;
    height: 300px;
    border-radius: 0.8rem;
    flex-shrink: 0;

    @media (max-width: 768px) {
        width: 100%;
        height: 240px;
    }
}

.detail-info {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.detail-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 1rem;
}

.detail-price-row {
    margin-bottom: 1rem;
}

.detail-price {
    font-size: 1.8rem;
    font-weight: 700;
    color: #ef4444;
}

.detail-meta {
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
    font-size: 0.85rem;
    color: #64748b;
    margin-bottom: 1rem;
}

.detail-desc {
    font-size: 0.9rem;
    color: #475569;
    line-height: 1.6;
    margin: 0 0 1rem;
}

.detail-order {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: auto;
    margin-bottom: 1rem;
}

.order-total {
    font-size: 1rem;
    font-weight: 600;
    color: #ef4444;
}

.buy-btn {
    width: 100%;
    height: 44px;
    font-size: 1rem;
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    border: none;
}

.detail-content {
    margin-top: 2rem;
    padding: 1.5rem;
    background: #fff;
    border-radius: 1rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    font-size: 0.9rem;
    color: #475569;
    line-height: 1.8;

    :deep(img) {
        max-width: 100%;
        border-radius: 0.5rem;
    }
}

/* 下单弹窗 */
:deep(.order-dialog) {
    border-radius: 1.2rem;
    overflow: hidden;

    .ant-modal-header {
        margin-right: 0;
        padding: 1.5rem 1.5rem 1rem;
        border-bottom: none;
    }

    .ant-modal-title {
        font-size: 1.3rem;
        font-weight: 700;
        color: #0f172a;
    }

    .ant-modal-body {
        padding: 0.5rem 1.5rem;
    }

    .ant-modal-footer {
        padding: 0.5rem 1.5rem 1.5rem;
    }

    .order-product {
        display: flex;
        gap: 1rem;
        padding: 1rem;
        background: #f8fafc;
        border-radius: 0.8rem;
        margin-bottom: 1.5rem;
    }

    .order-product-img {
        width: 80px;
        height: 80px;
        border-radius: 0.5rem;
        flex-shrink: 0;
    }

    .order-product-info {
        display: flex;
        flex-direction: column;
        justify-content: center;

        h3 {
            font-size: 1rem;
            font-weight: 600;
            color: #0f172a;
            margin: 0 0 0.3rem;
        }
    }

    .order-product-price {
        font-size: 0.9rem;
        color: #ef4444;
        font-weight: 600;
    }

    .order-summary {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.5rem;
        padding: 1rem 0;
        font-size: 1rem;
    }

    .order-amount {
        font-size: 1.4rem;
        font-weight: 700;
        color: #ef4444;
    }

    .submit-order-btn {
        width: 100%;
        height: 46px;
        font-size: 1rem;
        background: linear-gradient(135deg, #2563eb, #3b82f6);
        border: none;
    }

    /* 支付方式 */
    .pay-methods {
        display: flex;
        gap: 0.8rem;
        flex-wrap: wrap;
    }

    .pay-method-item {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.5rem 0.9rem;
        border: 2px solid #e2e8f0;
        border-radius: 0.6rem;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 0.85rem;
        color: #475569;

        &:hover {
            border-color: #3b82f6;
        }

        &.active {
            border-color: #3b82f6;
            background: #eff6ff;
            color: #2563eb;
        }
    }

    .pay-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 0.4rem;
        color: #fff;
        font-size: 0.75rem;
        font-weight: 700;

        &.alipay {
            background: #1677ff;
        }

        &.wechat {
            background: #07c160;
        }

        &.qq {
            background: #12b7f5;
        }

        &.usdt {
            background: #26a17b;
        }
    }
}
</style>
